import React, { useState } from 'react';
import { signIn, signOut, fetchUserAttributes, confirmSignIn } from '@aws-amplify/auth';
import { useNavigate } from 'react-router-dom';
import toastr from 'toastr';

const MSPLogin = ({ mspConfig }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [needsPasswordChange, setNeedsPasswordChange] = useState(false);
  const [signInStep, setSignInStep] = useState(null);
  const navigate = useNavigate();

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const signInOutput = await signIn({ username: email, password });
      
      if (signInOutput.nextStep?.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
        setNeedsPasswordChange(true);
        setSignInStep(signInOutput);
        toastr.info('Please set your new password');
        setLoading(false);
        return;
      }
      
      if (!signInOutput.isSignedIn) {
        throw new Error('Sign in failed');
      }
      
      // Get user attributes after successful sign in
      const attributes = await fetchUserAttributes();
      const userMspID = attributes['custom:mspID'];
      const userType = attributes['custom:userType'];
      
      if (userType !== 'msp' || userMspID !== mspConfig.mspID) {
        await signOut();
        toastr.error('Access denied. You do not belong to this MSP.');
        return;
      }

      toastr.success('Login successful!');
      window.location.href = '/dashboard';
    } catch (error) {
      console.error('Login error:', error);
      toastr.error(error.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    
    if (newPassword !== confirmPassword) {
      toastr.error('Passwords do not match');
      return;
    }
    
    if (newPassword.length < 8) {
      toastr.error('Password must be at least 8 characters');
      return;
    }
    
    setLoading(true);

    try {
      const confirmOutput = await confirmSignIn({ challengeResponse: newPassword });
      
      if (!confirmOutput.isSignedIn) {
        throw new Error('Password change failed');
      }
      
      // Get user attributes after successful sign in
      const attributes = await fetchUserAttributes();
      const userMspID = attributes['custom:mspID'];
      const userType = attributes['custom:userType'];
      
      if (userType !== 'msp' || userMspID !== mspConfig.mspID) {
        await signOut();
        toastr.error('Access denied. You do not belong to this MSP.');
        return;
      }

      toastr.success('Password changed successfully!');
      window.location.href = '/dashboard';
    } catch (error) {
      console.error('Password change error:', error);
      toastr.error(error.message || 'Password change failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container-fluid vh-100">
      <div className="row h-100">
        {/* Left Section - Branding */}
        <div className="col-lg-8 col-md-6 d-flex align-items-center justify-content-center" 
             style={{ backgroundColor: mspConfig.branding?.primaryColor || '#342ead' }}>
          <div className="text-center text-white">
            {mspConfig.branding?.logo ? (
              <img src={mspConfig.branding.logo} alt={mspConfig.name} className="mb-4" style={{ maxHeight: '100px' }} />
            ) : (
              <img src="/images/noventiq-logo.svg" alt={mspConfig.name} className="mb-4" style={{ maxHeight: '100px', filter: 'brightness(0) invert(1)' }} />
            )}
            <h2 className="fw-bold mb-4">{mspConfig.name}</h2>
            <p className="lead fs-4">
              Cloud Management Portal
            </p>
            <p className="fs-5">
              Manage multiple customer environments from one unified platform
            </p>
          </div>
        </div>

        {/* Right Section - Login Form */}
        <div className="col-lg-4 col-md-6 d-flex align-items-center">
          <div className="w-100 px-4">
            <div className="text-center mb-4">
              <h3>Portal Login</h3>
              <p className="text-muted">Sign in to your portal</p>
            </div>

            <form onSubmit={needsPasswordChange ? handlePasswordChange : handleLogin}>
              <div className="mb-3">
                <input
                  type="email"
                  className="form-control"
                  placeholder="Email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  disabled={needsPasswordChange}
                />
              </div>

              {!needsPasswordChange ? (
                <div className="mb-3">
                  <input
                    type="password"
                    className="form-control"
                    placeholder="Password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                  />
                </div>
              ) : (
                <>
                  <div className="alert alert-info">
                    <small>Please set a new password for your account</small>
                  </div>
                  <div className="mb-3">
                    <input
                      type="password"
                      className="form-control"
                      placeholder="New Password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      required
                    />
                  </div>
                  <div className="mb-3">
                    <input
                      type="password"
                      className="form-control"
                      placeholder="Confirm New Password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      required
                    />
                  </div>
                </>
              )}

              <button
                type="submit"
                className="btn w-100"
                style={{ 
                  backgroundColor: mspConfig.branding?.primaryColor || '#342ead',
                  color: 'white'
                }}
                disabled={loading}
              >
                {loading ? 'Processing...' : (needsPasswordChange ? 'Set New Password' : 'Sign In')}
              </button>
            </form>

            <div className="text-center mt-4">
              <small className="text-muted">
                Powered by ODIR
              </small>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MSPLogin;